import {
  GetSubmissionQuery,
  ReviewerStatus,
  ReviewMode,
  SignatureStatus,
  SignatureType,
  SubmissionDocumentType,
  SubmissionStatus,
} from "generated-graphql/graphql";
import {
  getReviewersSubmissionDocumentReview,
  SubmissionDocumentReviewWithReviewer,
} from "hooks/submission/submissionReviewHelpers";
import { CurrentUser } from "providers/user";

// Define the minimal reviewer type needed for determineReviewTurn
// Export this interface so it can be used in the return type
export interface MinimalReviewer {
  reviewOrder: number;
  isSigner?: boolean;
  user: {
    id: string;
  };
  submissionDocumentReviews: Array<{
    completedAt?: any;
    reviewerStatus?: string | null;
  }>;
}

/**
 * Determine if it's the current user's turn to review.
 * This includes signers as signers are reviews with a special flag
 * and will be ordered last in the review order.
 */
export function determineReviewTurn({
  reviewOrderingEnforced,
  currentUser,
  reviewers,
}: {
  reviewOrderingEnforced: boolean;
  currentUser: CurrentUser | null | undefined;
  reviewers: MinimalReviewer[];
}): {
  isCurrentUsersTurn: boolean;
  nextReviewer: MinimalReviewer | null;
} {
  if (!reviewOrderingEnforced || !currentUser) {
    return { isCurrentUsersTurn: true, nextReviewer: null };
  }

  // Make sure the reviewers are sorted by reviewOrder
  const sortedReviewers = [...reviewers].sort((a, b) => a.reviewOrder - b.reviewOrder);

  // Find the first reviewer who hasn't completed
  // reviewing all of their documents
  const pendingReviewer = sortedReviewers.find((reviewer) => {
    return reviewer.submissionDocumentReviews.some(
      (review) => !review.completedAt && !review.reviewerStatus
    );
  });

  // If no pending reviewer, everyone has completed
  if (!pendingReviewer) {
    return { isCurrentUsersTurn: false, nextReviewer: null };
  }

  // Check if the current user is the pending reviewer
  const isCurrentUsersTurn = pendingReviewer.user.id === currentUser.id;

  return { isCurrentUsersTurn, nextReviewer: pendingReviewer };
}

export enum SubmissionReviewState {
  // Reviewer and signer states
  NO_DOCUMENTS_ASSIGNED = "NO_DOCUMENTS_ASSIGNED",
  DOCUMENT_REVIEW_COMPLETED = "DOCUMENT_REVIEW_COMPLETED",

  // Reviewer states
  REVIEWER_WAITING_FOR_TURN = "REVIEWER_WAITING_FOR_TURN",
  REVIEWER_CAN_REVIEW = "REVIEWER_CAN_REVIEW",

  // Signer states
  SIGNER_IS_WAITING_FOR_REVIEWERS = "SIGNER_IS_WAITING_FOR_REVIEWERS",
  SIGNER_CAN_SIGN = "SIGNER_CAN_SIGN",
}

export type SubmissionReviewStates = {
  currentUsersSubmissionDocumentReview: SubmissionDocumentReviewWithReviewer | null;
  isReviewerASigner: boolean;
  nextReviewer: MinimalReviewer | null;
  reviewState: SubmissionReviewState;
};

export function computeSubmissionReviewStates(params: {
  currentUser: CurrentUser | null | undefined;
  submissionData: GetSubmissionQuery | undefined;
}): SubmissionReviewStates {
  const { currentUser, submissionData } = params;

  const submission = submissionData?.submission;

  // Get the most recent submission document review for the current user
  const currentUsersSubmissionDocumentReview = getReviewersSubmissionDocumentReview(
    submission,
    currentUser?.id
  );

  const currentUserIsAReviewer = submission?.reviewers?.find((r) => r.user.id === currentUser?.id);

  const allNonSignerReviewersHaveApproved = !!submission?.reviewers
    ?.filter((reviewer) => !reviewer.isSigner)
    .every((reviewer) => {
      // Only check reviews on active documents (not rejected submissions)
      const activeReviews = reviewer.submissionDocumentReviews.filter(
        (review) =>
          review.submissionDocument?.type === SubmissionDocumentType.Submission ||
          review.submissionDocument?.type === SubmissionDocumentType.SignedSubmission
      );
      // Must have at least one active review and all must be approved
      return (
        activeReviews.length > 0 &&
        activeReviews.every((review) => review.reviewerStatus === ReviewerStatus.Approved)
      );
    });

  const reviewOrderingEnforced = submission?.reviewMode === ReviewMode.Sequential;

  const { isCurrentUsersTurn, nextReviewer } = determineReviewTurn({
    reviewOrderingEnforced,
    currentUser,
    reviewers: submission?.reviewers || [],
  });

  function buildReviewState(): SubmissionReviewState {
    // During ENCAMP_INITIAL_REVIEW, only staff reviewers can take action.
    // Non-staff reviewers must wait until the submission moves to IN_REVIEW.
    if (
      submission?.status === SubmissionStatus.EncampInitialReview &&
      currentUserIsAReviewer &&
      !currentUserIsAReviewer.isStaff
    ) {
      return SubmissionReviewState.NO_DOCUMENTS_ASSIGNED;
    }

    // Reviewer/Signer/User does not have any submission documents to review
    if (!currentUsersSubmissionDocumentReview) {
      return SubmissionReviewState.NO_DOCUMENTS_ASSIGNED;
    }

    // Reviewer/signer has completed their review/signature (or rejected)
    if (
      currentUsersSubmissionDocumentReview.completedAt ||
      currentUsersSubmissionDocumentReview.reviewerStatus
    ) {
      return SubmissionReviewState.DOCUMENT_REVIEW_COMPLETED;
    }

    // Check the signers states first (but only for digital signatures)
    // WET_INK signers should follow the reviewer flow
    if (currentUserIsAReviewer?.isSigner && submission?.signatureType === SignatureType.Digital) {
      if (allNonSignerReviewersHaveApproved) {
        return SubmissionReviewState.SIGNER_CAN_SIGN;
      }

      return SubmissionReviewState.SIGNER_IS_WAITING_FOR_REVIEWERS;
    }

    // Then check the reviewer states (includes WET_INK signers so they can
    // still review before receiving the document in the mail)
    if (reviewOrderingEnforced && !isCurrentUsersTurn) {
      return SubmissionReviewState.REVIEWER_WAITING_FOR_TURN;
    }
    return SubmissionReviewState.REVIEWER_CAN_REVIEW;
  }

  return {
    currentUsersSubmissionDocumentReview,
    isReviewerASigner: currentUserIsAReviewer?.isSigner ?? false,
    nextReviewer,
    reviewState: buildReviewState(),
  };
}
